import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for tracking user activity sessions
 * Handles: session start, heartbeat, idle detection, session end
 * OPTIMIZED: Reduced event listeners, throttled heartbeats
 */
export const useActivityTracking = () => {
  const sessionIdRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isIdleRef = useRef(false);
  const heartbeatIntervalRef = useRef(null);
  const idleTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const throttleRef = useRef(null);
  
  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  const HEARTBEAT_INTERVAL = 60 * 1000; // 60 seconds (was 30)
  const ACTIVITY_THROTTLE = 5000; // Throttle activity events to every 5 seconds
  
  const getToken = () => localStorage.getItem('token');
  
  // Get browser info (cached)
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Unknown';
  };
  
  const getDeviceInfo = () => {
    if (/Mobi|Android/i.test(navigator.userAgent)) return 'Mobile';
    if (/Tablet|iPad/i.test(navigator.userAgent)) return 'Tablet';
    return 'Desktop';
  };
  
  const getCurrentModule = () => {
    const path = window.location.pathname;
    const modules = {
      'dashboard': 'Dashboard',
      'leads': 'Leads',
      'tasks': 'Tasks',
      'forms': 'Forms',
      'candidate': 'Candidate Portal',
      'employer': 'Employer Portal',
      'admin': 'Admin Panel',
      'workforce': 'Workforce Analytics'
    };
    for (const [key, value] of Object.entries(modules)) {
      if (path.includes(key)) return value;
    }
    return 'Dashboard';
  };
  
  // Start session - only once
  const startSession = useCallback(async () => {
    if (isInitializedRef.current) return;
    
    const token = getToken();
    if (!token) return;
    
    // Check for existing session
    const existingSession = localStorage.getItem('workforceSessionId');
    if (existingSession) {
      sessionIdRef.current = existingSession;
      isInitializedRef.current = true;
      return;
    }
    
    try {
      const response = await axios.post(`${API}/workforce/session/start`, {
        browser: getBrowserInfo(),
        device: getDeviceInfo(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000 // 5 second timeout
      });
      
      sessionIdRef.current = response.data.session_id;
      localStorage.setItem('workforceSessionId', response.data.session_id);
      isInitializedRef.current = true;
    } catch (error) {
      // Silently fail - don't block app loading
      console.debug('Activity tracking unavailable');
    }
  }, []);
  
  // Send heartbeat - optimized with error handling
  const sendHeartbeat = useCallback(async () => {
    const token = getToken();
    const sessionId = sessionIdRef.current || localStorage.getItem('workforceSessionId');
    
    if (!token || !sessionId) return;
    
    try {
      await axios.post(`${API}/workforce/session/heartbeat`, {
        session_id: sessionId,
        is_active: !isIdleRef.current,
        current_page: window.location.pathname,
        current_module: getCurrentModule()
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
    } catch (error) {
      // Silently fail
    }
  }, []);
  
  // Throttled activity handler
  const handleActivity = useCallback(() => {
    // Throttle activity events
    if (throttleRef.current) return;
    
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
    }, ACTIVITY_THROTTLE);
    
    lastActivityRef.current = Date.now();
    
    if (isIdleRef.current) {
      isIdleRef.current = false;
      sendHeartbeat();
    }
    
    // Reset idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    
    idleTimeoutRef.current = setTimeout(() => {
      isIdleRef.current = true;
      sendHeartbeat();
    }, IDLE_TIMEOUT);
  }, [sendHeartbeat]);
  
  // End session
  const endSession = useCallback(async () => {
    const token = getToken();
    const sessionId = sessionIdRef.current || localStorage.getItem('workforceSessionId');
    
    if (!sessionId) return;
    
    // Clear all intervals/timeouts
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    if (throttleRef.current) clearTimeout(throttleRef.current);
    
    if (token) {
      try {
        await axios.post(`${API}/workforce/session/end`, {
          session_id: sessionId
        }, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 3000
        });
      } catch (error) {
        // Ignore errors on session end
      }
    }
    
    sessionIdRef.current = null;
    localStorage.removeItem('workforceSessionId');
    isInitializedRef.current = false;
  }, []);
  
  // Log activity (for manual logging)
  const logActivity = useCallback(async (action, module, recordName = null, recordId = null, details = null) => {
    const token = getToken();
    if (!token) return;
    
    try {
      await axios.post(`${API}/workforce/activity`, {
        action,
        module,
        record_name: recordName,
        record_id: recordId,
        details
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
    } catch (error) {
      // Silently fail
    }
  }, []);
  
  // Initialize - delayed to not block page load
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    
    // Delay initialization by 2 seconds to not block initial page load
    const initTimeout = setTimeout(() => {
      startSession();
      
      // Start heartbeat with delay
      heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    }, 2000);
    
    // Minimal event listeners - only essential ones
    const events = ['mousedown', 'keydown', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    // Visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isIdleRef.current = true;
      } else {
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup
    return () => {
      clearTimeout(initTimeout);
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (throttleRef.current) clearTimeout(throttleRef.current);
    };
  }, [startSession, handleActivity, sendHeartbeat]);
  
  return {
    startSession,
    endSession,
    logActivity,
    sessionId: sessionIdRef.current
  };
};

export default useActivityTracking;
